import { useState } from 'react';
import type { Cohort } from '../../types';
import {
  AddButton,
  Button,
  Checkbox,
  Field,
  Input,
  RemoveButton,
  Section,
  TextArea,
  cx,
} from '../ui/primitives';
import { newCohort } from '../../state/draft';

/**
 * Cohorts & Signals.
 *
 * Two modes over the same data:
 *   - list (default): the compact, scrollable, checkbox list from the design,
 *     showing name, bulleted signals and size range
 *   - edit: an expanded row for the cohort being changed
 *
 * The design shows the list; editing has to live somewhere, so a row expands in
 * place rather than opening a separate screen.
 */

function signalLines(signals: string): string[] {
  return signals
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function CohortRow({
  cohort,
  index,
  editing,
  onToggle,
  onEdit,
  onDoneEditing,
  onChange,
  onRemove,
  canRemove,
  error,
}: {
  cohort: Cohort;
  index: number;
  editing: boolean;
  onToggle: (selected: boolean) => void;
  onEdit: () => void;
  onDoneEditing: () => void;
  onChange: (patch: Partial<Cohort>) => void;
  onRemove: () => void;
  canRemove: boolean;
  error?: string;
}) {
  const lines = signalLines(cohort.signals);

  if (editing) {
    return (
      <li className="border-t border-line-soft bg-accent-soft/30 px-4 py-4 first:border-t-0">
        {/*
          The editing row is a form rather than a table row, so it keeps the
          screen's width and stacks — there is nothing to align it against.
        */}
        <div className="flex min-w-0 items-start gap-3">
          <Checkbox
            checked={cohort.selected}
            onChange={onToggle}
            label={`Include cohort ${index + 1}`}
            className="mt-1.5"
          />
          <span className="mt-1 w-8 shrink-0 text-label text-ink-muted">#{index + 1}</span>

          <div className="min-w-0 flex-1 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_11rem]">
              <Field label="Cohort Name" htmlFor={`cohort-name-${cohort.id}`}>
                <Input
                  id={`cohort-name-${cohort.id}`}
                  value={cohort.name}
                  placeholder="e.g. Ultra Luxury Affluent Audience"
                  onChange={(event) => onChange({ name: event.target.value })}
                />
              </Field>
              <Field label="Audience Size" htmlFor={`cohort-size-${cohort.id}`}>
                <Input
                  id={`cohort-size-${cohort.id}`}
                  value={cohort.size}
                  placeholder="e.g. 4 - 6 Mn"
                  onChange={(event) => onChange({ size: event.target.value })}
                />
              </Field>
            </div>

            <Field
              label="Signals"
              htmlFor={`cohort-signals-${cohort.id}`}
              help="One signal per line — age, income, behaviour, intent, location."
            >
              <TextArea
                id={`cohort-signals-${cohort.id}`}
                rows={6}
                value={cohort.signals}
                placeholder={
                  'Age: 28-55, Male/Female, Senior professionals, Business owners, CXOs.\nIncome: High, High Credit Card Spend.\nIntent: Luxury shopping, premium brand engagement\nLocation: Metros + Tier 1 cities'
                }
                onChange={(event) => onChange({ signals: event.target.value })}
              />
            </Field>

            <Field label="Remarks" htmlFor={`cohort-remarks-${cohort.id}`} hint="(optional)">
              <Input
                id={`cohort-remarks-${cohort.id}`}
                value={cohort.remarks}
                placeholder="e.g. Parents with kids of 6-12 years"
                onChange={(event) => onChange({ remarks: event.target.value })}
              />
            </Field>

            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={onDoneEditing} className="px-4 py-2">
                Done
              </Button>
              {canRemove ? (
                <Button variant="danger" onClick={onRemove} className="px-4 py-2">
                  Remove cohort
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li
      className={cx(
        'group border-t border-line-soft px-4 py-3.5 first:border-t-0',
        error && 'bg-danger-soft',
      )}
    >
      <div className="flex min-w-[46rem] items-start gap-3 md:min-w-0">
        <Checkbox
          checked={cohort.selected}
          onChange={onToggle}
          label={`Include cohort ${index + 1}`}
          className="mt-0.5"
        />

        <span className="w-8 shrink-0 text-label text-ink-muted">#{index + 1}</span>

        {/* A fixed width at every size now, so the rows line up under the header. */}
        <div className="w-52 shrink-0">
          <p className={cx('text-label', cohort.name ? 'text-ink' : 'italic text-ink-faint')}>
            {cohort.name || 'Untitled cohort'}
          </p>
        </div>

        <div className="min-w-0 flex-1">
          {lines.length ? (
            <ul className="space-y-0.5">
              {lines.map((line, position) => (
                <li
                  key={`${cohort.id}-signal-${position}`}
                  className="flex gap-1.5 text-label text-ink-soft"
                >
                  <span aria-hidden="true" className="mt-[3px] shrink-0 leading-none">
                    &bull;
                  </span>
                  <span className="min-w-0 break-words">{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-label italic text-ink-faint">No signals listed</p>
          )}
          {error ? <p className="mt-1 text-micro text-danger">{error}</p> : null}
        </div>

        <span className="w-20 shrink-0 text-right text-label text-ink-muted">
          {cohort.size || '—'}
        </span>

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="rounded px-2 py-1 text-micro font-medium text-accent hover:bg-accent-soft focus-visible:ring-2 focus-visible:ring-accent/40"
          >
            Edit
          </button>
          {canRemove ? (
            <RemoveButton onClick={onRemove} label={`Remove cohort ${index + 1}`} />
          ) : null}
        </div>
      </div>
    </li>
  );
}

export function CohortsSection({
  cohorts,
  onChange,
  errors,
  sectionError,
}: {
  cohorts: Cohort[];
  onChange: (cohorts: Cohort[]) => void;
  /** Keyed by cohort index. */
  errors: Record<number, string | undefined>;
  sectionError?: string;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const allSelected = cohorts.length > 0 && cohorts.every((cohort) => cohort.selected);

  const patchAt = (index: number, patch: Partial<Cohort>) => {
    onChange(cohorts.map((cohort, position) => (position === index ? { ...cohort, ...patch } : cohort)));
  };

  const removeAt = (index: number) => {
    const target = cohorts[index];
    if (target && editingId === target.id) setEditingId(null);
    onChange(cohorts.filter((_, position) => position !== index));
  };

  const addCohort = () => {
    const cohort = newCohort();
    onChange([...cohorts, cohort]);
    // A new cohort is empty, so open it for editing straight away.
    setEditingId(cohort.id);
  };

  return (
    <Section
      title="Cohorts &amp; Signals"
      action={
        cohorts.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange(cohorts.map((cohort) => ({ ...cohort, selected: !allSelected })))}
            className="text-field font-medium text-accent hover:text-accent-hover hover:underline"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        ) : null
      }
    >
      {cohorts.length ? (
        <div className="overflow-x-auto rounded-input border border-line">
          {/*
            Scrolls sideways on a narrow screen rather than squeezing. Six things
            share this row — a checkbox, a number, the cohort name, its signals,
            its size and the actions — and on a phone the signals column, which
            holds the longest text by far, was compressed to a word or two per
            line. A minimum width keeps every column readable and lets the reader
            pan across; the alternative, stacking into cards, loses the alignment
            that makes a column of sizes comparable at a glance.
          */}
          {/*
            Column labels over the list. The design screenshot shows the rows
            unlabelled, but once values arrive from a spreadsheet or a screenshot
            the reviewer needs to see which extracted field landed where — these
            name the three destinations explicitly.
          */}
          <div
            aria-hidden="true"
            className="flex min-w-[46rem] items-center gap-3 border-b border-line bg-canvas px-4 py-2 md:min-w-0"
          >
            <span className="w-[18px] shrink-0" />
            <span className="w-8 shrink-0" />
            <span className="w-full min-w-0 text-micro font-semibold uppercase tracking-wide text-ink-muted sm:w-52 sm:shrink-0">
              Cohort
            </span>
            <span className="min-w-0 flex-1 text-micro font-semibold uppercase tracking-wide text-ink-muted">
              Signals
            </span>
            <span className="w-20 shrink-0 text-right text-micro font-semibold uppercase tracking-wide text-ink-muted">
              Audience Size
            </span>
            <span className="w-[76px] shrink-0" />
          </div>

          <ul className="scroll-thin max-h-[22rem] overflow-y-auto">
            {cohorts.map((cohort, index) => (
              <CohortRow
                key={cohort.id}
                cohort={cohort}
                index={index}
                editing={editingId === cohort.id}
                onToggle={(selected) => patchAt(index, { selected })}
                onEdit={() => setEditingId(cohort.id)}
                onDoneEditing={() => setEditingId(null)}
                onChange={(patch) => patchAt(index, patch)}
                onRemove={() => removeAt(index)}
                canRemove={cohorts.length > 1}
                error={errors[index]}
              />
            ))}
          </ul>
        </div>
      ) : (
        <div className="rounded-input border border-dashed border-line-strong px-4 py-8 text-center">
          <p className="text-field text-ink-muted">No cohorts yet.</p>
          <p className="mt-1 text-micro text-ink-faint">
            Add one to describe the audience this rollout needs.
          </p>
        </div>
      )}

      {sectionError ? (
        <p role="alert" className="text-micro text-danger">
          {sectionError}
        </p>
      ) : null}

      <AddButton onClick={addCohort}>Add Cohort</AddButton>
    </Section>
  );
}
